import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useWeather, type WeatherData } from '@/contexts/WeatherContext'
import { useHover } from '@/hooks/useHover'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'
import { theme } from '@/lib/theme'
import Button from './ui/Button'

// Weather symbol component
const WeatherSymbol = ({ symbol, colors }: { symbol: string; colors: any }) => {
  const symbolStyles = {
    fontSize: theme.fontSize.lg,
    fontFamily: theme.fontFamily.mono,
    fontWeight: 'bold',
    color: colors.primaryText,
  }

  const symbolMap: { [key: string]: string } = {
    SUN: '☀',
    CLOUD: '☁',
    RAIN: '🌧',
    SNOW: '❄',
    FOG: '🌫',
    STORM: '⛈',
    UNKNOWN: '?',
  }

  return <div style={symbolStyles}>{symbolMap[symbol] || '?'}</div>
}

export default function WeatherWidget() {
  const { t, i18n } = useTranslation()
  const { setWeather: setContextWeather, setLocation: setContextLocation } =
    useWeather()
  const { colors } = useThemeMode()
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [locationPermission, setLocationPermission] = useState<
    'granted' | 'denied' | 'prompt'
  >('prompt')
  const [useFahrenheit, setUseFahrenheit] = useState(i18n.language !== 'ja') // Celsius for Japanese, Fahrenheit otherwise
  const { isHovered: isTempHovered, hoverProps: tempHoverProps } = useHover()

  const toggleTemperatureUnit = () => {
    setUseFahrenheit(!useFahrenheit)
  }

  const fetchWeatherData = useCallback(
    async (lat: number, lon: number) => {
      try {
        setLoading(true)
        setError(null)

        // Fetch weather data once in Fahrenheit
        const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&timezone=auto&forecast_days=1&temperature_unit=fahrenheit`

        const response = await fetch(weatherUrl)

        if (!response.ok) {
          throw new Error('Failed to fetch weather data')
        }

        const weatherData = await response.json()

        // Map weather codes to translation keys and symbols
        const weatherCodeMap: {
          [key: number]: { translationKey: string; symbol: string }
        } = {
          0: { translationKey: 'weather.clearSky', symbol: 'SUN' },
          1: { translationKey: 'weather.mainlyClear', symbol: 'SUN' },
          2: { translationKey: 'weather.partlyCloudy', symbol: 'CLOUD' },
          3: { translationKey: 'weather.overcast', symbol: 'CLOUD' },
          45: { translationKey: 'weather.foggy', symbol: 'FOG' },
          48: { translationKey: 'weather.depositingRimeFog', symbol: 'FOG' },
          51: { translationKey: 'weather.lightDrizzle', symbol: 'RAIN' },
          53: { translationKey: 'weather.moderateDrizzle', symbol: 'RAIN' },
          55: { translationKey: 'weather.denseDrizzle', symbol: 'RAIN' },
          56: {
            translationKey: 'weather.lightFreezingDrizzle',
            symbol: 'SNOW',
          },
          57: {
            translationKey: 'weather.denseFreezingDrizzle',
            symbol: 'SNOW',
          },
          61: { translationKey: 'weather.slightRain', symbol: 'RAIN' },
          63: { translationKey: 'weather.moderateRain', symbol: 'RAIN' },
          65: { translationKey: 'weather.heavyRain', symbol: 'RAIN' },
          66: { translationKey: 'weather.lightFreezingRain', symbol: 'SNOW' },
          67: { translationKey: 'weather.heavyFreezingRain', symbol: 'SNOW' },
          71: { translationKey: 'weather.slightSnow', symbol: 'SNOW' },
          73: { translationKey: 'weather.moderateSnow', symbol: 'SNOW' },
          75: { translationKey: 'weather.heavySnow', symbol: 'SNOW' },
          77: { translationKey: 'weather.snowGrains', symbol: 'SNOW' },
          80: { translationKey: 'weather.slightRainShowers', symbol: 'RAIN' },
          81: { translationKey: 'weather.moderateRainShowers', symbol: 'RAIN' },
          82: { translationKey: 'weather.violentRainShowers', symbol: 'RAIN' },
          85: { translationKey: 'weather.slightSnowShowers', symbol: 'SNOW' },
          86: { translationKey: 'weather.heavySnowShowers', symbol: 'SNOW' },
          95: { translationKey: 'weather.thunderstorm', symbol: 'STORM' },
          96: {
            translationKey: 'weather.thunderstormSlightHail',
            symbol: 'STORM',
          },
          99: {
            translationKey: 'weather.thunderstormHeavyHail',
            symbol: 'STORM',
          },
        }

        const current = weatherData.current
        const weatherCode = current.weather_code
        const weatherInfo = weatherCodeMap[weatherCode] || {
          translationKey: 'weather.unknown',
          symbol: 'UNKNOWN',
        }

        // Convert Fahrenheit to Celsius
        const tempF = current.temperature_2m
        const tempC = ((tempF - 32) * 5) / 9

        const formattedWeather: WeatherData = {
          temperatureC: Math.round(tempC),
          temperatureF: Math.round(tempF),
          unitC: '°C',
          unitF: '°F',
          description: t(weatherInfo.translationKey),
          icon: weatherInfo.symbol,
          humidity: current.relative_humidity_2m,
          windSpeed: Math.round(current.wind_speed_10m * 10) / 10,
          windUnit: weatherData.current_units.wind_speed_10m,
          timezone: weatherData.timezone,
          lastUpdated: current.time,
        }

        setWeather(formattedWeather)
        setContextWeather(formattedWeather)
      } catch (err) {
        console.error('Weather fetch error:', err)
        setError(err instanceof Error ? err.message : t('weather.loadError'))
      } finally {
        setLoading(false)
      }
    },
    [setContextWeather, t]
  )

  const requestLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError(t('weather.geoNotSupported'))
      return
    }

    setLoading(true)

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        setLocationPermission('granted')

        // Fetch location name via reverse geocoding
        try {
          const geocodeUrl = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          const geocodeResponse = await fetch(geocodeUrl, {
            headers: {
              'User-Agent': 'WeatherWidget/1.0',
            },
          })

          if (geocodeResponse.ok) {
            const geocodeData = await geocodeResponse.json()
            const address = geocodeData.address || {}

            setContextLocation({
              latitude,
              longitude,
              city: address.city || address.town || address.village,
              state: address.state,
              country: address.country,
              displayName: geocodeData.display_name,
            })
          } else {
            setContextLocation({ latitude, longitude })
          }
        } catch (err) {
          console.error('Geocoding error:', err)
          setContextLocation({ latitude, longitude })
        }

        fetchWeatherData(latitude, longitude)
      },
      (err) => {
        console.error('Geolocation error:', err)
        setLocationPermission('denied')
        setError(t('weather.locationDenied'))
        setLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 600000, // 10 minutes
      }
    )
  }, [fetchWeatherData, setContextLocation, t])

  useEffect(() => {
    // Try to get location on component mount
    if (locationPermission === 'prompt') {
      requestLocation()
    }
  }, [locationPermission, requestLocation])

  if (error) {
    return (
      <div
        style={{
          backgroundColor: colors.errorBg,
          border: `1px solid ${colors.errorBorder}`,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing.md,
          boxShadow: theme.shadows.sm,
          textAlign: 'center',
          width: '220px',
        }}
      >
        <div
          style={{
            color: colors.darkText,
            fontSize: theme.fontSize.sm,
            marginBottom: theme.spacing.sm,
            fontWeight: 'bold',
          }}
        >
          {t('weather.error')}
        </div>
        <div
          style={{
            fontSize: theme.fontSize.xs,
            color: colors.secondaryText,
            marginBottom: theme.spacing.sm,
          }}
        >
          {error}
        </div>
        {locationPermission === 'denied' && (
          <Button
            variant="primary"
            size="sm"
            onClick={requestLocation}
            style={{
              fontSize: theme.fontSize.xs,
            }}
          >
            {t('weather.retry')}
          </Button>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: colors.primaryBg,
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing.md,
          boxShadow: theme.shadows.sm,
          textAlign: 'center',
          width: '220px',
        }}
      >
        <LoadingText
          {...loadingPresets.widget(t('weather.loading'), colors.secondaryText)}
          fontSize={theme.fontSize.sm}
        />
      </div>
    )
  }

  if (!weather) {
    return (
      <div
        style={{
          backgroundColor: colors.primaryBg,
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing.md,
          boxShadow: theme.shadows.sm,
          textAlign: 'center',
          width: '220px',
        }}
      >
        <Button
          variant="primary"
          size="md"
          onClick={requestLocation}
          style={{
            fontSize: theme.fontSize.sm,
            fontWeight: 'bold',
          }}
        >
          {t('weather.getWeather')}
        </Button>
      </div>
    )
  }

  return (
    <div
      style={{
        backgroundColor: colors.primaryBg,
        border: `1px solid ${colors.primaryBorder}`,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        boxShadow: theme.shadows.sm,
        transition: theme.transitions.boxShadow,
        cursor: 'default',
        width: '220px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = theme.shadows.md
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = theme.shadows.sm
      }}
    >
      {/* Header row with icon, temperature */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: theme.spacing.sm,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
          }}
        >
          <WeatherSymbol symbol={weather.icon} colors={colors} />
          <button
            onClick={toggleTemperatureUnit}
            {...tempHoverProps}
            style={{
              fontSize: theme.fontSize.lg,
              fontWeight: 'bold',
              color: isTempHovered ? colors.hoverAccent : colors.primaryText,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: theme.transitions.default,
              padding: '0',
            }}
            title={
              useFahrenheit
                ? t('weather.switchToCelsius')
                : t('weather.switchToFahrenheit')
            }
          >
            {useFahrenheit ? weather.temperatureF : weather.temperatureC}°
            {useFahrenheit ? 'F' : 'C'}
          </button>
        </div>
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: theme.fontSize.sm,
          color: colors.primaryText,
          marginBottom: theme.spacing.sm,
          fontWeight: '500',
        }}
      >
        {weather.description}
      </div>

      {/* Details */}
      <div
        style={{
          fontSize: theme.fontSize.xs,
          color: colors.secondaryText,
          lineHeight: '1.4',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '2px',
          }}
        >
          <span>{t('weather.humidity')}</span>
          <span style={{ color: colors.primaryText }}>{weather.humidity}%</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>{t('weather.wind')}</span>
          <span style={{ color: colors.primaryText }}>
            {weather.windSpeed} {weather.windUnit}
          </span>
        </div>
      </div>
    </div>
  )
}
